/**
 * PromptsSection — Admin UI for viewing and editing all AI prompts.
 *
 * Shows all registered prompts grouped by module.
 * Highlights overridden prompts. Edit inline with save/reset to default.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminService } from '../../services/admin.service'
import type { SystemPrompt } from '../../services/admin.service'
import toast from 'react-hot-toast'

const MODULE_LABELS: Record<string, string> = {
  lead_gen: 'Лидогенерация',
  content_intelligence: 'Content Intelligence',
}

export default function PromptsSection() {
  const queryClient = useQueryClient()
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [filterModule, setFilterModule] = useState<string>('all')

  const { data: prompts, isLoading } = useQuery({
    queryKey: ['adminPrompts'],
    queryFn: () => adminService.getPrompts(),
  })

  const updateMutation = useMutation({
    mutationFn: ({ key, text }: { key: string; text: string }) =>
      adminService.updatePrompt(key, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPrompts'] })
      toast.success('Промпт обновлён')
      setEditingKey(null)
    },
    onError: () => toast.error('Ошибка сохранения'),
  })

  const resetMutation = useMutation({
    mutationFn: (key: string) => adminService.resetPrompt(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPrompts'] })
      toast.success('Сброшено на дефолт')
    },
    onError: () => toast.error('Ошибка сброса'),
  })

  const startEdit = (prompt: SystemPrompt) => {
    setEditingKey(prompt.key)
    setEditText(prompt.prompt_text)
  }

  const cancelEdit = () => {
    setEditingKey(null)
    setEditText('')
  }

  const saveEdit = (key: string) => {
    updateMutation.mutate({ key, text: editText })
  }

  const toggleExpand = (key: string) => {
    setExpandedKey(expandedKey === key ? null : key)
  }

  // Group by module
  const modules = Array.from(new Set(prompts?.map((p) => p.module) ?? []))
  const filtered = filterModule === 'all'
    ? prompts
    : prompts?.filter((p) => p.module === filterModule)

  if (isLoading) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">AI Промпты</h2>
        <div className="animate-pulse space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          AI Промпты
          <span className="ml-2 text-sm font-normal text-gray-400">
            {prompts?.length ?? 0} шт.
          </span>
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setFilterModule('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${
              filterModule === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Все
          </button>
          {modules.map((m) => (
            <button
              key={m}
              onClick={() => setFilterModule(m)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                filterModule === m
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {MODULE_LABELS[m] || m}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {filtered?.map((prompt) => (
          <div
            key={prompt.key}
            className={`border rounded-lg transition ${
              !prompt.is_default
                ? 'border-blue-200 bg-blue-50/30'
                : 'border-gray-200 bg-white'
            }`}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer"
              onClick={() => editingKey !== prompt.key && toggleExpand(prompt.key)}
            >
              <div className="flex items-center gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 text-sm">
                      {prompt.title}
                    </span>
                    <code className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                      {prompt.key}
                    </code>
                    {!prompt.is_default && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                        изменён
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{prompt.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">
                  {MODULE_LABELS[prompt.module] || prompt.module}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    expandedKey === prompt.key ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Expanded content */}
            {(expandedKey === prompt.key || editingKey === prompt.key) && (
              <div className="px-4 pb-4 border-t border-gray-100">
                {editingKey === prompt.key ? (
                  /* ── Edit mode ── */
                  <div className="mt-3 space-y-3">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={Math.min(20, editText.split('\n').length + 3)}
                      className="w-full font-mono text-sm border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      spellCheck={false}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {editText.length} символов
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={cancelEdit}
                          className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                          Отмена
                        </button>
                        <button
                          onClick={() => saveEdit(prompt.key)}
                          disabled={updateMutation.isPending}
                          className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── View mode ── */
                  <div className="mt-3 space-y-3">
                    <pre className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">
                      {prompt.prompt_text}
                    </pre>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-400">
                        {prompt.updated_at && (
                          <span>
                            Изменён: {new Date(prompt.updated_at).toLocaleString('ru-RU')}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {!prompt.is_default && (
                          <button
                            onClick={() => resetMutation.mutate(prompt.key)}
                            disabled={resetMutation.isPending}
                            className="px-3 py-1.5 text-xs text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                          >
                            Сбросить на дефолт
                          </button>
                        )}
                        <button
                          onClick={() => startEdit(prompt)}
                          className="px-3 py-1.5 text-xs text-white bg-gray-800 rounded-lg hover:bg-gray-900"
                        >
                          Редактировать
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
